import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { useMutation } from 'convex/react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useFastActions } from '@/components/number-habit'
import { useOnlineStatus } from '@/hooks/use-online-status'
import { completeNode, uncompleteNode } from '@/lib/nodes-optimistic'
import { playDoneSound } from '@/lib/sounds'
import { undoToast } from '@/lib/undo-toast'
import { useOfflineStore } from '@/store/offline.store'

// Ticks a task from a goal card (online or queued offline) with an Undo toast.
export function useCompleteWithUndo(): (taskId: string) => void {
  const { t } = useTranslation()
  const complete = useMutation(api.nodes.complete).withOptimisticUpdate((localStore, { taskId }) =>
    completeNode(localStore, taskId),
  )
  const uncomplete = useMutation(api.nodes.uncomplete).withOptimisticUpdate(
    (localStore, { taskId }) => uncompleteNode(localStore, taskId),
  )
  const online = useOnlineStatus()
  const enqueue = useOfflineStore((s) => s.enqueue)
  const fast = useFastActions()

  return (taskId) => {
    if (fast.isFastHabit(taskId)) return void fast.toggle(taskId as Id<'nodes'>)
    playDoneSound()
    if (online)
      complete({ taskId: taskId as Id<'nodes'> }).catch(() => toast.error(t('common.error')))
    else enqueue({ kind: 'complete', args: { taskId } })
    undoToast(t('today.markedDone'), () => {
      if (online) uncomplete({ taskId: taskId as Id<'nodes'> }).catch(() => {})
      else enqueue({ kind: 'uncomplete', args: { taskId } })
    })
  }
}
